import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "src/common/state/hooks";
import { Input } from "../../../common/components/Input";
import { ButtonText } from "../../../common/components/Button";
import { Error } from "../../../common/components/AppState/Error";
import { IsLoading } from "../../../common/components/AppState/IsLoading";
import { LoadingState, setError, setIsLoading } from "../../../common/state/slice/appStateSlice";
import { resendMfaChallenge, verifyMfa } from "../state/authSlice";
import {
  formatCountdown,
  useCountdown,
} from "../../graph/components/AssistantScenarIaRail/FailedTurnCard";
import { LOGIN_COMPONENT_ID } from "./LoginForm";

const CODE_PATTERN = /^\d{6}$/;
const RESEND_COOLDOWN_SECONDS = 30;
const RESEND_WINDOW_MS = 15 * 60 * 1000;
const MAX_RESENDS_PER_WINDOW = 3;
const MAX_VERIFY_ATTEMPTS = 5;
const RATE_LIMIT_COOLDOWN_SECONDS = 60;

const focusCodeField = () => {
  setTimeout(() => {
    document.querySelector<HTMLInputElement>("[data-testid='mfa-challenge-code']")?.focus();
  }, 0);
};

interface MfaChallengeFormProps {
  mfaToken: string;
  method: "totp" | "email" | null;
  sentTo?: string | null;
  onBackToCredentials: () => void;
}

const MfaChallengeForm = ({
  mfaToken,
  method,
  sentTo = null,
  onBackToCredentials,
}: MfaChallengeFormProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [code, setCode] = useState("");
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSent, setResendSent] = useState(false);

  const [resendCount, setResendCount] = useState(0);
  const [resendWindowStart, setResendWindowStart] = useState<number | null>(null);

  const [failedAttempts, setFailedAttempts] = useState(0);

  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);

  const autoSubmittedCodeRef = useRef<string | null>(null);

  const isTerminal = failedAttempts >= MAX_VERIFY_ATTEMPTS;
  const rateLimitRemainingSeconds = useCountdown(rateLimitedUntil ?? undefined);
  const isRateLimited = rateLimitedUntil !== null && rateLimitRemainingSeconds > 0;

  useEffect(() => {
    dispatch(setIsLoading({ componentId: LOGIN_COMPONENT_ID, loading: LoadingState.Idle }));
  }, [dispatch]);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined;
    }
    const timer = setInterval(() => setResendCooldown((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (resendWindowStart === null) {
      return undefined;
    }
    const remaining = RESEND_WINDOW_MS - (Date.now() - resendWindowStart);
    if (remaining <= 0) {
      setResendCount(0);
      setResendWindowStart(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      setResendCount(0);
      setResendWindowStart(null);
    }, remaining);
    return () => clearTimeout(timer);
  }, [resendWindowStart]);

  useEffect(() => {
    if (rateLimitedUntil !== null && rateLimitRemainingSeconds <= 0) {
      setRateLimitedUntil(null);
      dispatch(setError({ componentId: LOGIN_COMPONENT_ID, error: "" }));
    }
  }, [rateLimitRemainingSeconds, rateLimitedUntil, dispatch]);

  const resendLimitReached = resendCount >= MAX_RESENDS_PER_WINDOW;

  const handleToggleRecoveryCode = () => {
    setUseRecoveryCode(!useRecoveryCode);
    setCode("");
  };

  const submitCode = useCallback(() => {
    setIsSubmitting(true);
    dispatch(
      verifyMfa({
        componentId: LOGIN_COMPONENT_ID,
        mfaToken,
        ...(useRecoveryCode ? { recoveryCode: code.trim() } : { otp: code.trim() }),
      }),
    )
      .unwrap()
      .then(() => {})
      .catch((err: { error?: { message?: string }; status?: number } | undefined) => {
        setIsSubmitting(false);
        setCode("");
        focusCodeField();

        if (err?.status === 429) {
          setRateLimitedUntil(Date.now() + RATE_LIMIT_COOLDOWN_SECONDS * 1000);
          return;
        }

        if (err?.error?.message === "mfa_challenge_invalid") {
          onBackToCredentials();
          return;
        }

        setFailedAttempts((n) => n + 1);
      });
  }, [code, dispatch, mfaToken, onBackToCredentials, useRecoveryCode]);

  useEffect(() => {
    if (useRecoveryCode || isSubmitting || isRateLimited || isTerminal) {
      return;
    }
    if (!CODE_PATTERN.test(code)) {
      autoSubmittedCodeRef.current = null;
      return;
    }
    if (autoSubmittedCodeRef.current === code) {
      return;
    }
    autoSubmittedCodeRef.current = code;
    submitCode();
  }, [code, isRateLimited, isSubmitting, isTerminal, submitCode, useRecoveryCode]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    submitCode();
  };

  const handleResend = () => {
    if (resendCooldown > 0 || resendLimitReached) {
      return;
    }
    setResendSent(false);
    dispatch(resendMfaChallenge({ componentId: LOGIN_COMPONENT_ID, mfaToken }))
      .unwrap()
      .then(() => {
        setResendSent(true);
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      })
      .catch(() => {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      })
      .finally(() => {
        setResendCount((c) => c + 1);
        setResendWindowStart((start) => start ?? Date.now());
      });
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(useRecoveryCode ? e.target.value : e.target.value.replace(/\D/g, "").slice(0, 6));
  };

  const handleCodePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (useRecoveryCode) {
      return;
    }
    e.preventDefault();
    setCode(e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6));
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="my-2">
        <h2 className="text-xl font-medium text-black">{t("pages.login.mfaChallenge.title")}</h2>
        <p className="text-sm">
          {isTerminal
            ? t("pages.login.mfaChallenge.tooManyAttemptsSubtitle")
            : useRecoveryCode
            ? t("pages.login.mfaChallenge.subtitleRecoveryCode")
            : method === "email"
            ? sentTo
              ? t("pages.login.mfaChallenge.subtitleEmailTo", { email: sentTo })
              : t("pages.login.mfaChallenge.subtitleEmail")
            : t("pages.login.mfaChallenge.subtitleTotp")}
        </p>
      </div>

      {isTerminal ? (
        <p className="text-sm text-red-600 font-medium my-2" role="alert">
          {t("pages.login.mfaChallenge.tooManyAttempts")}
        </p>
      ) : (
        <>
          {!isRateLimited && (
            <Error
              componentId={LOGIN_COMPONENT_ID}
              errorString={t("pages.login.mfaChallenge.invalidCode")}
            />
          )}
          <p
            className={`text-xs text-amber-600 my-1 ${isRateLimited ? "" : "invisible"}`}
            role="status"
          >
            {t("pages.login.mfaChallenge.rateLimited", {
              time: formatCountdown(rateLimitRemainingSeconds),
            })}
          </p>
          <IsLoading componentId={LOGIN_COMPONENT_ID} showSuccess />

          <Input
            autoFocus
            type="text"
            autoComplete={useRecoveryCode ? "off" : "one-time-code"}
            inputMode={useRecoveryCode ? "text" : "numeric"}
            maxLength={useRecoveryCode ? 19 : 6}
            readOnly={isSubmitting}
            value={code}
            onChange={handleCodeChange}
            onPaste={handleCodePaste}
            className="font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600 border-0 tracking-[0.3em] text-center"
            placeholder={
              useRecoveryCode
                ? t("pages.login.mfaChallenge.recoveryCodePlaceholder")
                : t("pages.login.mfaChallenge.codePlaceholder")
            }
            data-testid="mfa-challenge-code"
          />

          <div className="flex justify-between items-center mt-2">
            <ButtonText
              type="button"
              text={
                useRecoveryCode
                  ? t("pages.login.mfaChallenge.useCodeInstead")
                  : t("pages.login.mfaChallenge.useRecoveryCodeInstead")
              }
              onClick={handleToggleRecoveryCode}
            />

            <ButtonText
              type="button"
              className={method === "email" && !useRecoveryCode ? "" : "invisible"}
              text={
                resendLimitReached
                  ? t("pages.login.mfaChallenge.resendLimitReached")
                  : resendCooldown > 0
                  ? t("pages.login.mfaChallenge.resendCooldown", { seconds: resendCooldown })
                  : t("pages.login.mfaChallenge.resend")
              }
              onClick={handleResend}
              disabled={resendCooldown > 0 || resendLimitReached}
            />
          </div>

          <p
            className={`text-xs text-green-600 mt-1 ${
              resendSent && method === "email" ? "" : "invisible"
            }`}
          >
            {t("pages.login.mfaChallenge.resendSent")}
          </p>
        </>
      )}

      <div className="flex justify-between items-center pt-3">
        <ButtonText
          type="button"
          text={t("pages.login.mfaChallenge.backToLogin")}
          onClick={onBackToCredentials}
        />
        {!isTerminal && (
          <button
            className="btn-primary-fill"
            type="submit"
            disabled={
              isSubmitting ||
              isRateLimited ||
              (useRecoveryCode ? code.trim().length === 0 : !CODE_PATTERN.test(code.trim()))
            }
          >
            {t("pages.login.mfaChallenge.submit")}
          </button>
        )}
      </div>
    </form>
  );
};

export default MfaChallengeForm;
